/**
 * 监控看板 GraphQL 返回值 → 前端视图类型转换
 *
 * 后端下发的是算好的比率，前端不再做任何计算 —— 口径在服务端收口。
 * null 一律原样保留（不转 0）：null 表示当日未取到，0 表示确实为零，
 * 两者在「这个地址是否该被处罚」上含义完全不同。
 */
import {ExemptTargetType, MonitorLevel, MonitorRow} from '../types/ExemptSpotCheckType';
import {MonitorRowGql} from './queries/monitorQueries';

const toTargetType = (value?: string | null): ExemptTargetType =>
    value === ExemptTargetType.FENCE ? ExemptTargetType.FENCE : ExemptTargetType.ADDRESS;

/** 未知等级回落为空而非 NORMAL：判不出时展示「-」，不能误报成「正常」 */
const toMonitorLevel = (value?: string | null): MonitorLevel | undefined => {
    if (value && Object.values(MonitorLevel).includes(value as MonitorLevel)) {
        return value as MonitorLevel;
    }
    return undefined;
};

/** null → undefined：视图层统一按 undefined 判空 */
const orUndefined = (value?: number | null): number | undefined => value ?? undefined;

/**
 * 展示编号由类型 + ID 拼出（AD-9001 / FN-9101）。
 *
 * 后端不下发该字段：它是纯展示概念，落库反而要在两处维护。
 */
const buildTargetNo = (targetType: ExemptTargetType, targetId: number): string =>
    `${targetType === ExemptTargetType.FENCE ? 'FN' : 'AD'}-${targetId}`;

export const toMonitorRow = (dto: MonitorRowGql): MonitorRow => {
    const targetType = toTargetType(dto.targetType);
    const targetId = dto.targetId ?? 0;
    return {
        targetType,
        targetId,
        targetNo: buildTargetNo(targetType, targetId),
        // 展示字段由后端按 targetId 实时回填；目标已删除时为空，行只剩编号
        address: dto.address ?? '',
        zipcode: dto.zipcode ?? undefined,
        warehouseCode: dto.warehouseCode ?? undefined,
        addressCount: orUndefined(dto.addressCount),
        addressType: dto.addressType ?? undefined,
        dspName: dto.dspName ?? undefined,
        orderCount7d: orUndefined(dto.orderCount),
        complaintCount7d: orUndefined(dto.complaintCount),
        complaintCountDeltaRate: orUndefined(dto.complaintCountDeltaRate),
        monitorLevel: toMonitorLevel(dto.monitorLevel),
        triggerDetail: dto.triggerDetail ?? undefined,
        hasSpotTaskInPeriod: dto.hasSpotTaskInPeriod ?? false,
    };
};

export const toMonitorRows = (list: MonitorRowGql[]): MonitorRow[] => list.map(toMonitorRow);
